import React, { useEffect, useRef, useState } from "react";
import {
  Box, Typography, Chip,
  Tooltip, IconButton, CircularProgress, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper
} from "@mui/material";
import { CloudUpload, PictureAsPdf, InsertDriveFile, Image, Download, Delete } from "@mui/icons-material";
import { useApi } from "../utils/api";
import { ENDPOINTS } from "../config/endpoints";

const formatFileSize = (bytes) =>
  bytes
    ? bytes / 1024 < 1024
      ? `${(bytes / 1024).toFixed(1)} KB`
      : `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : "-";

const getFileIcon = (file) => {
  const ext = file.orijinal_ad?.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return <PictureAsPdf sx={{ color: "#EF4444" }} />;
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext))
    return <Image sx={{ color: "#10B981" }} />;
  return <InsertDriveFile />;
};

const Dosyalar = () => {
  const userData = JSON.parse(localStorage.getItem("user"));
  const isAdmin = userData?.type === "admin";

  const http = useApi();
  const [files, setFiles] = useState([]);
  const [departmanlar, setDepartmanlar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  // Departman bazında kurum dağılımı (admin için)
  const [kurumDagilimi, setKurumDagilimi] = useState([]);
  const [dagilimLoading, setDagilimLoading] = useState(false);

  const fetchDepartmanlar = async () => {
    try {
      const data = await http.get(ENDPOINTS.DEPARTMAN.LIST, { skip: 0, limit: 100 });
      setDepartmanlar(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Departmanlar alınamadı:", e);
    }
  };

  const fetchFiles = async () => {
    setLoading(true);
    setError("");
    try {
      const params = !isAdmin && userData?.departman_id
        ? { departman_id: userData.departman_id }
        : {};
      const data = await http.get(ENDPOINTS.GENERAL.LIST, params);
      setFiles(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Dosyalar alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartmanlar();
    fetchFiles();
  }, []);

  // Admin departman seçtiğinde kurum dağılımını getir
  useEffect(() => {
    if (!isAdmin || !selectedType) {
      setKurumDagilimi([]);
      return;
    }
    setDagilimLoading(true);
    http.get(`/stats/departman/${selectedType}/kurum-dagilimi`)
      .then((d) => setKurumDagilimi(Array.isArray(d) ? d : []))
      .catch(() => setKurumDagilimi([]))
      .finally(() => setDagilimLoading(false));
  }, [http, isAdmin, selectedType]);

  const uploadFiles = async (filesList) => {
    if (!selectedType) {
      setError("Lütfen bir departman seçin");
      return;
    }
    setUploading(true);
    setError("");
    const fd = new FormData();
    fd.append("tur", selectedType);
    Array.from(filesList).forEach(f => fd.append("files", f));
    try {
      await http.postForm(ENDPOINTS.GENERAL.UPLOAD, fd);
      fetchFiles();
    } catch (err) {
      setError(err.message || "Yüklenemedi");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files;
    if (dropped.length) uploadFiles(dropped);
  };

  const handleDownload = async (file) => {
    try {
      const blob = await http.getBlob(ENDPOINTS.GENERAL.DOWNLOAD(file.orijinal_ad));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.orijinal_ad;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("İndirme başarısız");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await http.del(ENDPOINTS.GENERAL.DELETE(deleteTarget.orijinal_ad));
      setDeleteTarget(null);
      fetchFiles();
    } catch (err) {
      setError(err.message || "Silme başarısız");
      setDeleteTarget(null);
    }
  };

  const personelDepLabel = !isAdmin
    ? departmanlar.find(d => d.id === userData?.departman_id)?.ad || null
    : null;

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">Genel Dosyalar</Typography>
        {!isAdmin && personelDepLabel && (
          <Chip label={`Departman: ${personelDepLabel}`} size="small" color="primary" variant="outlined" />
        )}
      </Box>

      {isAdmin && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Departman Seç (Dosya Türü)</InputLabel>
            <Select
              value={selectedType}
              label="Departman Seç (Dosya Türü)"
              onChange={e => setSelectedType(e.target.value)}
            >
              <MenuItem value=""><em>Seçiniz</em></MenuItem>
              {departmanlar.map(d => (
                <MenuItem key={d.id} value={d.id}>{d.ad}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedType && (
            <Box sx={{
              border: "1px solid #E5E7EB",
              borderRadius: 2,
              p: 2,
              mb: 2,
              bgcolor: "#F9FAFB"
            }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Bu departmanda personel sayıları (kurum bazında)
              </Typography>
              {dagilimLoading ? (
                <CircularProgress size={18} />
              ) : kurumDagilimi.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Bu departmanda hiç personel bulunmuyor.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {kurumDagilimi.map((k) => (
                    <Chip
                      key={k.kurum_id}
                      label={`${k.kurum_adi}: ${k.personel_sayisi}`}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                  <Chip
                    label={`Toplam: ${kurumDagilimi.reduce((a, b) => a + b.personel_sayisi, 0)}`}
                    color="success"
                  />
                </Box>
              )}
            </Box>
          )}

          <Box
            onDrop={handleDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            sx={{ border: "2px dashed #d1d5db", p: 4, textAlign: "center", borderRadius: 2, cursor: "pointer", mb: 2 }}
          >
            <CloudUpload sx={{ fontSize: 48, color: "#3b82f6" }} />
            <Typography>Dosyaları sürükleyin veya göz atın</Typography>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={e => uploadFiles(e.target.files)}
            />
            {uploading && <CircularProgress size={20} sx={{ mt: 1 }} />}
          </Box>
        </>
      )}

      {error && <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>}

      {loading ? (
        <LinearProgress sx={{ mt: 2 }} />
      ) : files.length === 0 ? (
        <Typography sx={{ mt: 4, textAlign: "center", color: "text.secondary" }}>
          {isAdmin ? "Henüz dosya yüklenmemiş" : "Departmanınıza ait dosya bulunamadı"}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                <TableCell sx={{ fontWeight: 700 }}>Dosya Adı</TableCell>
                {isAdmin && <TableCell sx={{ fontWeight: 700 }}>Departman</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">Boyut</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map(file => {
                const depLabel = isAdmin
                  ? departmanlar.find(d => String(d.id) === String(file.tur))?.ad || file.tur
                  : null;

                return (
                  <TableRow key={file.orijinal_ad} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {getFileIcon(file)}
                        <Typography
                          sx={{ ml: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={file.orijinal_ad}
                        >
                          {file.orijinal_ad}
                        </Typography>
                      </Box>
                    </TableCell>

                    {isAdmin && (
                      <TableCell>
                        {depLabel && (
                          <Chip label={depLabel} size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                    )}

                    <TableCell align="right">
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.boyut)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Tooltip title="İndir">
                          <IconButton size="small" onClick={() => handleDownload(file)}>
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {isAdmin && (
                          <Tooltip title="Sil">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(file)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Silme Onayı Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Dosya Silinsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            <strong>{deleteTarget?.orijinal_ad}</strong> dosyasını silmek istediğinizden emin misiniz?
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Vazgeç</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Evet, Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dosyalar;